import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SiteCtx = createContext(null);
const LS_KEY = "sl_active_site";

/**
 * SiteContext — the Pit Link portal is strictly site-scoped.
 *
 * How it works:
 *   • Every axios request adds an `X-Site-Override: <activeSiteCode>` header
 *     via lib/api.js (which reads localStorage["sl_active_site"]).
 *   • On login we default activeSite → user.site_code.
 *   • Admin/control/owner roles can switch to any other non-archived site
 *     they have access to (from /admin/sites). Non-admins stay locked to
 *     their single site.
 *   • Switching site writes localStorage and reloads the page so every data
 *     source re-fetches with the new header. Simple + safe.
 */
export function SiteProvider({ children }) {
  const { user } = useAuth();
  const [activeSite, setActiveSite] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [sites, setSites] = useState(null); // null = loading

  // Default active site to the user's own site on first login
  useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem(LS_KEY);
    if (!stored && user.site_code) {
      localStorage.setItem(LS_KEY, user.site_code);
      setActiveSite(user.site_code);
    } else if (stored) {
      setActiveSite(stored);
    }
  }, [user]);

  // Load the set of sites the user is allowed to switch between.
  useEffect(() => {
    if (!user) { setSites(null); return; }
    const roleL = String(user.role || "").toLowerCase();
    const isAdmin =
      user.is_admin ||
      ["admin", "control", "owner", "superintendent"].some((k) => roleL.includes(k));

    if (!isAdmin) {
      // Non-admin: only their own site
      const own = user.site
        ? [user.site]
        : user.site_code
        ? [{ code: user.site_code, site_name: user.site_code }]
        : [];
      setSites(own);
      return;
    }

    // Admin: pull the full site list. Filter out archived.
    (async () => {
      try {
        const { data } = await api.get("/admin/sites");
        const arr = (Array.isArray(data) ? data : data?.items || []).filter((s) => !s.archived);
        setSites(arr);
      } catch {
        // Fallback: at least the user's own site
        setSites(user.site ? [user.site] : []);
      }
    })();
  }, [user]);

  const switchSite = (code) => {
    if (!code || code === activeSite) return;
    localStorage.setItem(LS_KEY, code);
    // Full reload so every hook that already fetched data re-runs with the
    // new X-Site-Override header — simplest correct behaviour.
    window.location.reload();
  };

  const activeSiteObj = useMemo(
    () => (sites || []).find((s) => s.code === activeSite) || null,
    [sites, activeSite]
  );

  const canSwitch = (sites || []).length > 1;

  return (
    <SiteCtx.Provider value={{ activeSite, activeSiteObj, sites, canSwitch, switchSite }}>
      {children}
    </SiteCtx.Provider>
  );
}

export const useSite = () => useContext(SiteCtx) || {};
